import {
  KeyRound,
  LoaderCircle,
  Pencil,
  Search,
  Star,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import CambiarClaveUsuarioModal from '../components/usuarios/CambiarClaveUsuarioModal'
import CrearUsuarioModal from '../components/usuarios/CrearUsuarioModal'
import EditarAccesoModal from '../components/usuarios/EditarAccesoModal'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useAuth } from '../context/AuthContext'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'
import { obtenerRolInfo } from '../utils/usuarios'

function formatearFecha(valor) {
  if (!valor) return '—'

  const fecha = new Date(valor)

  if (Number.isNaN(fecha.getTime())) return '—'

  return fecha.toLocaleDateString('es-CL')
}

function Usuarios() {
  const { user } = useAuth()
  const { empresaActual, recargarEmpresas } = useEmpresa()
  const { modal, abrirModal, actualizarModal } = useModalMensaje()

  const esAdministrador = empresaActual?.rol === 'administrador'

  const [usuarios, setUsuarios] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [modalCrearAbierto, setModalCrearAbierto] = useState(false)
  const [relacionEditar, setRelacionEditar] = useState(null)
  const [relacionClave, setRelacionClave] = useState(null)
  const [actualizandoId, setActualizandoId] = useState(null)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setUsuarios([])
      setCargando(false)
      return
    }

    cargarUsuarios()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  const cargarUsuarios = async () => {
    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('usuarios_empresas')
        .select(`
          id,
          usuario_id,
          empresa_id,
          rol,
          es_predeterminada,
          activo,
          creado_en,
          perfil:perfiles (
            id,
            nombre_completo,
            correo,
            telefono,
            activo
          )
        `)
        .eq('empresa_id', empresaActual.id)
        .order('creado_en', { ascending: true })

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      setUsuarios(data || [])
    } catch (errorCarga) {
      console.error('Error al cargar usuarios:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar los usuarios.'
      )
      setUsuarios([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const usuariosFiltrados = usuarios.filter((relacion) => {
    const termino = busqueda.trim().toLowerCase()

    if (!termino) return true

    const nombreCoincide = (
      relacion.perfil?.nombre_completo || ''
    )
      .toLowerCase()
      .includes(termino)

    const correoCoincide = (relacion.perfil?.correo || '')
      .toLowerCase()
      .includes(termino)

    return nombreCoincide || correoCoincide
  })

  const abrirCrear = () => setModalCrearAbierto(true)
  const cerrarCrear = () => setModalCrearAbierto(false)

  const alCrear = () => {
    cerrarCrear()
    cargarUsuarios()

    abrirModal({
      tipo: 'success',
      titulo: 'Usuario creado',
      mensaje:
        'El usuario fue creado correctamente y ya puede iniciar sesión.',
    })
  }

  const abrirEdicionAcceso = (relacion) =>
    setRelacionEditar(relacion)
  const cerrarEdicionAcceso = () => setRelacionEditar(null)

  const alEditarAcceso = () => {
    cerrarEdicionAcceso()
    cargarUsuarios()

    abrirModal({
      tipo: 'success',
      titulo: 'Acceso actualizado',
      mensaje: 'El acceso fue actualizado correctamente.',
    })
  }

  const abrirCambiarClave = (relacion) => setRelacionClave(relacion)
  const cerrarCambiarClave = () => setRelacionClave(null)

  const alCambiarClave = () => {
    cerrarCambiarClave()

    abrirModal({
      tipo: 'success',
      titulo: 'Contraseña actualizada',
      mensaje:
        'La contraseña del usuario fue actualizada correctamente.',
    })
  }

  const confirmarQuitarUsuario = (relacion) => {
    if (relacion.usuario_id === user?.id) {
      abrirModal({
        tipo: 'warning',
        titulo: 'Acción no permitida',
        mensaje:
          'No puedes quitar tu propio acceso desde esta opción.',
      })
      return
    }

    abrirModal({
      tipo: 'confirm',
      titulo: 'Quitar de empresa',
      mensaje: `¿Deseas quitar a "${
        relacion.perfil?.nombre_completo || 'este usuario'
      }" de esta empresa? Conservará su acceso a otras empresas, si las tiene.`,
      textoAceptar: 'Quitar',
      textoCancelar: 'Cancelar',
      mostrarCancelar: true,
      onAceptar: () => ejecutarQuitarUsuario(relacion),
    })
  }

  const ejecutarQuitarUsuario = async (relacion) => {
    actualizarModal({
      procesando: true,
      mostrarCancelar: false,
      textoAceptar: 'Quitando...',
    })

    try {
      const { error: errorQuitar } = await supabase
        .from('usuarios_empresas')
        .delete()
        .eq('id', relacion.id)
        .eq('empresa_id', empresaActual.id)

      if (errorQuitar) {
        throw errorQuitar
      }

      await cargarUsuarios()

      abrirModal({
        tipo: 'success',
        titulo: 'Usuario quitado',
        mensaje: 'El usuario fue quitado de esta empresa correctamente.',
      })
    } catch (errorQuitar) {
      console.error('Error al quitar usuario:', errorQuitar)

      const mensaje = errorQuitar.message?.includes('administrador')
        ? 'No puedes quitar al último administrador de la empresa.'
        : errorQuitar.message ||
          'No fue posible quitar al usuario de la empresa.'

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo quitar el usuario',
        mensaje,
      })
    }
  }

  const confirmarEliminarUsuario = (relacion) => {
    if (relacion.usuario_id === user?.id) {
      abrirModal({
        tipo: 'warning',
        titulo: 'Acción no permitida',
        mensaje: 'No puedes eliminar tu propio usuario.',
      })
      return
    }

    abrirModal({
      tipo: 'confirm',
      titulo: 'Eliminar usuario',
      mensaje:
        'Esta acción eliminará definitivamente al usuario si no posee información histórica ni acceso a otras empresas. Esta acción no se puede deshacer.',
      textoAceptar: 'Eliminar',
      textoCancelar: 'Cancelar',
      mostrarCancelar: true,
      onAceptar: () => ejecutarEliminarUsuario(relacion),
    })
  }

  const ejecutarEliminarUsuario = async (relacion) => {
    actualizarModal({
      procesando: true,
      mostrarCancelar: false,
      textoAceptar: 'Eliminando...',
    })

    try {
      const { data, error: errorFuncion } =
        await supabase.functions.invoke('eliminar-usuario', {
          body: {
            usuario_id: relacion.usuario_id,
            empresa_id: empresaActual.id,
          },
        })

      if (errorFuncion) {
        throw errorFuncion
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      await cargarUsuarios()

      abrirModal({
        tipo: 'success',
        titulo: 'Usuario eliminado',
        mensaje: 'El usuario fue eliminado definitivamente.',
      })
    } catch (errorEliminar) {
      console.error('Error al eliminar usuario:', errorEliminar)

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo eliminar el usuario',
        mensaje:
          errorEliminar.message ||
          'No fue posible eliminar al usuario.',
      })
    }
  }

  const marcarPredeterminada = async (relacion) => {
    if (!relacion.activo || relacion.es_predeterminada) return

    try {
      setActualizandoId(relacion.id)

      const { error: errorDesmarcar } = await supabase
        .from('usuarios_empresas')
        .update({ es_predeterminada: false })
        .eq('usuario_id', relacion.usuario_id)
        .eq('es_predeterminada', true)
        .neq('id', relacion.id)

      if (errorDesmarcar) {
        throw errorDesmarcar
      }

      const { error: errorMarcar } = await supabase
        .from('usuarios_empresas')
        .update({ es_predeterminada: true })
        .eq('id', relacion.id)
        .eq('empresa_id', empresaActual.id)

      if (errorMarcar) {
        throw errorMarcar
      }

      if (!montadoRef.current) return

      await cargarUsuarios()

      if (relacion.usuario_id === user?.id) {
        await recargarEmpresas()
      }

      abrirModal({
        tipo: 'success',
        titulo: 'Empresa predeterminada actualizada',
        mensaje:
          'Se actualizó la empresa predeterminada correctamente.',
      })
    } catch (errorPredeterminada) {
      console.error(
        'Error al marcar empresa predeterminada:',
        errorPredeterminada
      )

      if (!montadoRef.current) return

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo actualizar',
        mensaje:
          errorPredeterminada.message ||
          'No fue posible marcar la empresa predeterminada.',
      })
    } finally {
      if (montadoRef.current) {
        setActualizandoId(null)
      }
    }
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <LoaderCircle
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando usuarios...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarUsuarios}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (usuarios.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <Users
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No hay usuarios registrados
          </h3>

          <p className="mt-2 text-slate-500">
            {esAdministrador
              ? 'Presiona “Crear usuario” para dar el primer acceso.'
              : 'Aún no hay usuarios con acceso a esta empresa.'}
          </p>

          {esAdministrador && (
            <button
              type="button"
              onClick={abrirCrear}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              <UserPlus size={19} />
              Crear usuario
            </button>
          )}
        </div>
      )
    }

    return (
      <>
        <div className="relative mb-4 max-w-sm">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            type="text"
            value={busqueda}
            onChange={(event) => setBusqueda(event.target.value)}
            placeholder="Buscar por nombre o correo"
            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          />
        </div>

        {usuariosFiltrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron usuarios para “{busqueda}”.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm md:min-w-[960px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Nombre</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Correo
                  </th>
                  <th className="px-6 py-3">Rol</th>
                  <th className="px-6 py-3">Estado</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Empresa predeterminada
                  </th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Fecha de incorporación
                  </th>
                  {esAdministrador && (
                    <th className="px-6 py-3 text-right">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {usuariosFiltrados.map((relacion) => {
                  const rolInfo = obtenerRolInfo(relacion.rol)

                  return (
                    <tr key={relacion.id}>
                      <td className="px-6 py-4 font-semibold text-slate-900">
                        {relacion.perfil?.nombre_completo ||
                          'Sin nombre'}
                        {relacion.usuario_id === user?.id && (
                          <span className="ml-2 text-xs font-normal text-slate-400">
                            (tú)
                          </span>
                        )}
                      </td>

                      <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                        {relacion.perfil?.correo || '—'}
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${rolInfo.clase}`}
                        >
                          {rolInfo.etiqueta}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        {relacion.activo ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                            Inactivo
                          </span>
                        )}
                      </td>

                      <td className="hidden px-6 py-4 md:table-cell">
                        {relacion.es_predeterminada ? (
                          <span className="flex items-center gap-1 text-xs font-semibold text-amber-600">
                            <Star
                              size={16}
                              className="fill-amber-500 text-amber-500"
                            />
                            Predeterminada
                          </span>
                        ) : esAdministrador &&
                          relacion.activo ? (
                          <button
                            type="button"
                            onClick={() =>
                              marcarPredeterminada(relacion)
                            }
                            disabled={
                              actualizandoId === relacion.id
                            }
                            className="flex items-center gap-1 text-xs font-semibold text-slate-500 transition hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {actualizandoId === relacion.id ? (
                              <LoaderCircle
                                size={14}
                                className="animate-spin"
                              />
                            ) : (
                              <Star size={14} />
                            )}
                            Marcar predeterminada
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">
                            —
                          </span>
                        )}
                      </td>

                      <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                        {formatearFecha(relacion.creado_en)}
                      </td>

                      {esAdministrador && (
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                abrirEdicionAcceso(relacion)
                              }
                              title="Editar acceso"
                              aria-label={`Editar acceso de ${
                                relacion.perfil
                                  ?.nombre_completo || ''
                              }`}
                              className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                            >
                              <Pencil size={16} />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                abrirCambiarClave(relacion)
                              }
                              title="Cambiar contraseña"
                              aria-label={`Cambiar contraseña de ${
                                relacion.perfil
                                  ?.nombre_completo || ''
                              }`}
                              className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                            >
                              <KeyRound size={16} />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                confirmarQuitarUsuario(relacion)
                              }
                              title="Quitar de empresa"
                              aria-label={`Quitar de la empresa a ${
                                relacion.perfil
                                  ?.nombre_completo || ''
                              }`}
                              className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                            >
                              <UserMinus size={16} />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                confirmarEliminarUsuario(relacion)
                              }
                              title="Eliminar definitivamente"
                              aria-label={`Eliminar definitivamente a ${
                                relacion.perfil
                                  ?.nombre_completo || ''
                              }`}
                              className="rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Mantenedores
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Usuarios y accesos
          </h2>

          <p className="mt-2 text-slate-500">
            Administra quién tiene acceso a{' '}
            {empresaActual?.nombre_fantasia}.
          </p>
        </div>

        {esAdministrador && (
          <button
            type="button"
            onClick={abrirCrear}
            className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
          >
            <UserPlus size={20} />
            Crear usuario
          </button>
        )}
      </div>

      {renderContenido()}

      {modalCrearAbierto && (
        <CrearUsuarioModal
          empresaId={empresaActual?.id}
          onClose={cerrarCrear}
          onGuardado={alCrear}
        />
      )}

      {relacionEditar && (
        <EditarAccesoModal
          relacion={relacionEditar}
          onClose={cerrarEdicionAcceso}
          onGuardado={alEditarAcceso}
        />
      )}

      {relacionClave && (
        <CambiarClaveUsuarioModal
          relacion={relacionClave}
          empresaId={empresaActual?.id}
          onClose={cerrarCambiarClave}
          onGuardado={alCambiarClave}
        />
      )}

      <ModalMensaje {...modal} />
    </div>
  )
}

export default Usuarios
